import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { CalendarClock, Landmark, Plus, Trash2 } from 'lucide-react';
import { DateField } from '../../../components/shared/DateField';

// Structured entry for a facility's accounts (credit agreement Section 7) and
// payment calendar (Section 1.01) when there is no machine-readable document.
// It produces the same contract terms the document lane extracts, under the
// same maker/checker activation (2x2 rule: nothing requires a PDF). Account
// numbers are masked to their last four digits before they leave the browser.

export type AccountType = 'COLLECTION' | 'LOCKBOX' | 'RESERVE' | 'PAYMENT' | 'DISTRIBUTION' | 'OTHER';
export interface AccountDraft {
  id: string; type: AccountType; name: string; bank: string; number: string;
  control: string; sweepToId: string; requiredPct: string; sourceClause: string;
}
export interface CalendarDraft {
  firstPeriodStart: string; determinationDay: string; paymentDay: string;
  convention: 'FOLLOWING' | 'MODIFIED_FOLLOWING' | 'PRECEDING';
  reportDue: string; bbcFrequency: string; sourceClause: string;
}
export interface CollectionsSchedulesDraft { accounts: AccountDraft[]; calendar: CalendarDraft | null; }

const TYPE_NAME: Record<AccountType, string> = {
  COLLECTION: 'Collection Account', LOCKBOX: 'Lockbox Account', RESERVE: 'Reserve Account',
  PAYMENT: 'Payment Account', DISTRIBUTION: 'Distribution Account', OTHER: 'Account',
};
let seq = 0;
const newAccount = (type: AccountType, bank = ''): AccountDraft => ({
  id: `acct-${Date.now().toString(36)}-${(seq += 1)}`, type, name: TYPE_NAME[type], bank, number: '',
  control: type === 'PAYMENT' ? 'Maintained by the Paying Agent' : '', sweepToId: '', requiredPct: '', sourceClause: '',
});
export const emptyCollectionsSchedules = (): CollectionsSchedulesDraft => ({ accounts: [], calendar: null });
const standardCalendar = (start: string): CalendarDraft => ({
  firstPeriodStart: start, determinationDay: '10', paymentDay: '15', convention: 'FOLLOWING',
  reportDue: 'DETERMINATION_DATE', bbcFrequency: 'MONTHLY_AND_EACH_BORROWING', sourceClause: '',
});

const digits = (value: string) => value.replace(/\D/g, '');
export const accountKeys = (accounts: AccountDraft[]) => {
  const counts: Record<string, number> = {};
  const keys: Record<string, string> = {};
  accounts.forEach(account => {
    counts[account.type] = (counts[account.type] || 0) + 1;
    keys[account.id] = counts[account.type] === 1 ? account.type : `${account.type}_${counts[account.type]}`;
  });
  return keys;
};

/** Slice 6 FR-6.2: prefill accounts and calendar from the active version's live rows. */
// Accepts both the raw extraction rows and the typed FacilityContractVersion
// records (interfaces carry no index signature, so they are widened here).
export const collectionsSchedulesFromVersion = (input: {
  contractAccounts?: ReadonlyArray<object> | null;
  paymentCalendar?: object | null;
  schedulesInForce?: { accounts?: ReadonlyArray<object> | null; paymentCalendar?: object | null } | null;
}): CollectionsSchedulesDraft => {
  // F-S6-021: a version that predates carry-forward holds no rows of its own; the schedules in
  // force for it are the nearest in-force version's (read model `schedulesInForce`). Prefill
  // from those, never from a default.
  const raw = input as { contractAccounts?: Record<string, unknown>[] | null; paymentCalendar?: Record<string, unknown> | null;
    schedulesInForce?: { accounts?: Record<string, unknown>[] | null; paymentCalendar?: Record<string, unknown> | null } | null };
  const version = {
    contractAccounts: raw.contractAccounts?.length ? raw.contractAccounts : raw.schedulesInForce?.accounts || [],
    paymentCalendar: raw.paymentCalendar || raw.schedulesInForce?.paymentCalendar || null,
  };
  const text = (value: unknown) => value === null || value === undefined ? '' : String(value);
  const accounts: AccountDraft[] = (version.contractAccounts || []).map(account => ({
    ...newAccount((text(account.account_type) || 'OTHER') as AccountType, text(account.bank_name)),
    name: text(account.account_name) || TYPE_NAME[(text(account.account_type) || 'OTHER') as AccountType],
    number: text(account.masked_account_number).replace(/\D/g, ''),
    control: text(account.control_agreement),
    requiredPct: text(account.required_balance_pct),
    sourceClause: text(account.source_clause),
  }));
  const byKey = new Map<string, AccountDraft>();
  (version.contractAccounts || []).forEach((account, index) => byKey.set(text(account.account_key), accounts[index]));
  (version.contractAccounts || []).forEach((account, index) => {
    const target = byKey.get(text(account.sweep_to_account_key));
    if (target) accounts[index].sweepToId = target.id;
  });
  const calendar = version.paymentCalendar ? {
    firstPeriodStart: text(version.paymentCalendar.first_period_start).slice(0, 10),
    determinationDay: text(version.paymentCalendar.determination_day),
    paymentDay: text(version.paymentCalendar.payment_day),
    convention: (text(version.paymentCalendar.business_day_convention) || 'FOLLOWING') as CalendarDraft['convention'],
    reportDue: text(version.paymentCalendar.servicer_report_due) || 'DETERMINATION_DATE',
    bbcFrequency: text(version.paymentCalendar.bbc_frequency) || 'MONTHLY_AND_EACH_BORROWING',
    sourceClause: text(version.paymentCalendar.source_clause),
  } : null;
  return { accounts, calendar };
};

export const validateCollectionsSchedules = (draft: CollectionsSchedulesDraft): string | null => {
  for (let index = 0; index < draft.accounts.length; index += 1) {
    const account = draft.accounts[index];
    if (!account.name.trim() || !account.bank.trim()) return `Account ${index + 1} needs a name and a bank`;
    if (account.number && digits(account.number).length < 4) return `Account ${index + 1}: enter at least the last four digits, or leave the number blank to complete it later`;
    if (account.requiredPct !== '' && (Number(account.requiredPct) < 0 || Number(account.requiredPct) > 100)) return `Account ${index + 1}: the required balance must be between 0% and 100%`;
    if (account.sweepToId && !draft.accounts.some(other => other.id === account.sweepToId)) return `Account ${index + 1} sweeps to an account that was removed`;
  }
  const calendar = draft.calendar;
  if (calendar) {
    const det = Number(calendar.determinationDay); const pay = Number(calendar.paymentDay);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(calendar.firstPeriodStart)) return 'Enter the date the first collection period starts';
    if (!Number.isInteger(det) || det < 1 || det > 31) return 'The determination day must be between 1 and 31';
    if (!Number.isInteger(pay) || pay < 1 || pay > 31) return 'The payment day must be between 1 and 31';
    if (pay < det) return 'The payment day cannot come before the determination day';
  }
  return null;
};

/** Contract-term payload shared by manual onboarding and manual amendments. */
export const collectionsSchedulesPayload = (draft: CollectionsSchedulesDraft) => {
  const keys = accountKeys(draft.accounts);
  const accounts = draft.accounts.map(account => ({
    account_key: keys[account.id],
    account_type: account.type,
    account_name: account.name.trim(),
    bank_name: account.bank.trim(),
    masked_account_number: digits(account.number).length >= 4 ? `****${digits(account.number).slice(-4)}` : null,
    control_agreement: account.control.trim() || null,
    sweep_to_account_key: account.sweepToId ? keys[account.sweepToId] : null,
    sweep_frequency: account.sweepToId ? 'DAILY' : null,
    required_balance_pct: account.requiredPct === '' ? null : Number(account.requiredPct),
    required_balance_basis: account.requiredPct === '' ? null : 'AGGREGATE_RECEIVABLES_BALANCE',
    source_clause: account.sourceClause.trim() || null,
  }));
  const calendar = draft.calendar ? {
    collection_period: 'CALENDAR_MONTH',
    first_period_start: draft.calendar.firstPeriodStart,
    determination_day: Number(draft.calendar.determinationDay),
    payment_day: Number(draft.calendar.paymentDay),
    business_day_convention: draft.calendar.convention,
    holiday_calendar: 'US_FEDERAL',
    servicer_report_due: draft.calendar.reportDue,
    loan_tape_due: draft.calendar.reportDue,
    borrowing_base_certificate_frequency: draft.calendar.bbcFrequency,
    source_clause: draft.calendar.sourceClause.trim() || null,
  } : null;
  return { accounts: accounts.length ? accounts : null, paymentCalendar: calendar };
};

const Wrap = styled.section`
  margin-top: .8rem; padding: .85rem; border: 1px solid rgba(56,189,248,.25); border-radius: 10px;
  background: rgba(2,132,199,.055); display: grid; gap: .6rem;
`;
const Head = styled.div`
  display: flex; justify-content: space-between; gap: 1rem; align-items: flex-start;
  h3 { margin: 0; color: #e2e8f0; font-size: .86rem; display: flex; align-items: center; gap: .4rem; }
  p { margin: .25rem 0 0; color: #94a3b8; font-size: .68rem; line-height: 1.45; max-width: 64ch; }
`;
const Row = styled.div`padding: .65rem; border: 1px solid rgba(148,163,184,.16); border-radius: 9px; background: rgba(15,23,42,.55); display: grid; gap: .45rem;`;
const Fields = styled.div`display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: .45rem; align-items: end; @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0,1fr)); }`;
const Label = styled.label`
  display: flex; flex-direction: column; gap: .22rem; color: #94a3b8; font-size: .61rem;
  input, select { min-width: 0; border: 1px solid rgba(148,163,184,.2); border-radius: 7px; box-sizing: border-box; width: 100%; height: 2.2rem; padding: .48rem .55rem; background: #0f172a; color: #e2e8f0; font-family: inherit; font-size: .68rem; }
  small { color: #64748b; font-size: .58rem; }
`;
const Actions = styled.div`display: flex; flex-wrap: wrap; gap: .4rem; align-items: center;`;
const Button = styled.button`
  display: inline-flex; gap: .3rem; align-items: center; border: 1px solid rgba(56,189,248,.25); border-radius: 7px;
  padding: .42rem .55rem; background: rgba(14,165,233,.1); color: #bae6fd; font-size: .65rem; cursor: pointer;
  &:disabled { opacity: .5; cursor: not-allowed; }
`;
const Periods = styled.div`display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: .45rem; @media (max-width: 900px) { grid-template-columns: repeat(2, minmax(0,1fr)); }`;
const Period = styled.div`
  padding: .5rem .55rem; border: 1px solid rgba(148,163,184,.14); border-radius: 9px; background: rgba(15,23,42,.5); display: grid; gap: .15rem;
  .m { color: #f8fafc; font-weight: 800; font-size: .7rem; }
  .r { display: flex; justify-content: space-between; gap: .4rem; color: #94a3b8; font-size: .6rem; }
  .r span:last-child { color: #e2e8f0; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: .58rem; }
  .note { color: #fde68a; font-size: .58rem; line-height: 1.35; }
`;
const Hint = styled.div<{ $tone?: 'warn' }>`color: ${p => p.$tone === 'warn' ? '#fde68a' : '#94a3b8'}; font-size: .64rem; line-height: 1.4;`;

interface PreviewPeriod { periodLabel: string; periodStart: string; periodEnd: string; determinationDate: string; paymentDate: string; rollNotes: string[]; }
const short = (value: string, weekday = false) => {
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString('en-US', weekday ? { weekday: 'short', month: 'short', day: 'numeric' } : { month: 'short', day: 'numeric' });
};
const monthLabel = (label: string) => { const [y, m] = label.split('-').map(Number); return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }); };

interface Props {
  value: CollectionsSchedulesDraft;
  onChange: (value: CollectionsSchedulesDraft) => void;
  effectiveFrom?: string;
  authorityHint?: string;
  idPrefix: string;
  /** Onboarding may start a calendar on the facility's effective date; an amendment never defaults the first period start (F-S6-021). */
  lane?: 'NEW_FACILITY' | 'AMENDMENT';
}

const CollectionsSchedulesEditor: React.FC<Props> = ({ value, onChange, effectiveFrom, authorityHint, idPrefix, lane = 'NEW_FACILITY' }) => {
  const [preview, setPreview] = useState<{ periods: PreviewPeriod[]; problems: string[] } | null>(null);
  const timer = useRef<number | null>(null);
  const calendar = value.calendar;

  useEffect(() => {
    if (timer.current) window.clearTimeout(timer.current);
    if (!calendar || validateCollectionsSchedules({ accounts: [], calendar })) { setPreview(null); return; }
    timer.current = window.setTimeout(() => {
      axios.post('/api/credit/abf/round2/payment-calendar/preview', {
        paymentCalendar: collectionsSchedulesPayload({ accounts: [], calendar }).paymentCalendar,
        effectiveFrom: calendar.firstPeriodStart,
      }).then(response => setPreview({ periods: response.data?.periods || [], problems: response.data?.problems || [] }))
        .catch(() => setPreview(null));
    }, 350);
    return () => { if (timer.current) window.clearTimeout(timer.current); };
  }, [calendar]);

  const update = (id: string, patch: Partial<AccountDraft>) => onChange({ ...value, accounts: value.accounts.map(account => account.id === id ? { ...account, ...patch } : account) });
  const addStandard = () => {
    const bank = value.accounts[0]?.bank || '';
    const collection = newAccount('COLLECTION', bank);
    const lockbox = { ...newAccount('LOCKBOX', bank), sweepToId: collection.id };
    onChange({ ...value, accounts: [...value.accounts, collection, lockbox, newAccount('RESERVE', bank), newAccount('PAYMENT')] });
  };
  const setCalendar = (patch: Partial<CalendarDraft>) => onChange({ ...value, calendar: { ...(calendar as CalendarDraft), ...patch } });
  const problem = validateCollectionsSchedules(value);

  return <Wrap aria-label="Accounts and payment calendar">
    <Head><div>
      <h3><Landmark size={15} /> Accounts and payment calendar</h3>
      <p>The accounts the servicer and paying agent use, and the calendar that creates each collection period. Enter them from the agreement or the account bank's documents; they become contract terms when a checker activates the version. Rows without a source clause cite {authorityHint ? <strong>{authorityHint}</strong> : 'the authority reference'}.</p>
    </div></Head>

    {value.accounts.map((account, index) => <Row key={account.id}>
      <Fields>
        <Label htmlFor={`${idPrefix}-type-${index}`}>Account<select id={`${idPrefix}-type-${index}`} value={account.type} onChange={e => update(account.id, { type: e.target.value as AccountType, name: account.name === TYPE_NAME[account.type] ? TYPE_NAME[e.target.value as AccountType] : account.name })}>{(Object.keys(TYPE_NAME) as AccountType[]).map(type => <option key={type} value={type}>{TYPE_NAME[type].replace(' Account', '') || 'Other'}</option>)}</select></Label>
        <Label htmlFor={`${idPrefix}-name-${index}`}>Name<input id={`${idPrefix}-name-${index}`} value={account.name} onChange={e => update(account.id, { name: e.target.value })} /></Label>
        <Label htmlFor={`${idPrefix}-bank-${index}`}>Bank<input id={`${idPrefix}-bank-${index}`} value={account.bank} onChange={e => update(account.id, { bank: e.target.value })} placeholder="Account bank or paying agent" /></Label>
        <Label htmlFor={`${idPrefix}-number-${index}`}>Account number<input id={`${idPrefix}-number-${index}`} inputMode="numeric" autoComplete="off" value={account.number} onChange={e => update(account.id, { number: e.target.value })} placeholder="Leave blank to add later" /><small>{digits(account.number).length >= 4 ? `Kept as ****${digits(account.number).slice(-4)}` : 'Only the last four digits are kept'}</small></Label>
        <Label htmlFor={`${idPrefix}-control-${index}`}>Control<input id={`${idPrefix}-control-${index}`} value={account.control} onChange={e => update(account.id, { control: e.target.value })} placeholder="e.g. DACA dated Sep 3, 2026" /></Label>
        {account.type === 'LOCKBOX' && <Label htmlFor={`${idPrefix}-sweep-${index}`}>Sweeps daily to<select id={`${idPrefix}-sweep-${index}`} value={account.sweepToId} onChange={e => update(account.id, { sweepToId: e.target.value })}><option value="">No sweep</option>{value.accounts.filter(other => other.id !== account.id).map(other => <option key={other.id} value={other.id}>{other.name || TYPE_NAME[other.type]}</option>)}</select></Label>}
        {account.type === 'RESERVE' && <Label htmlFor={`${idPrefix}-required-${index}`}>Required balance, % of receivables<input id={`${idPrefix}-required-${index}`} type="number" step="any" min="0" max="100" value={account.requiredPct} onChange={e => update(account.id, { requiredPct: e.target.value })} /></Label>}
        <Label htmlFor={`${idPrefix}-clause-${index}`}>Source clause<input id={`${idPrefix}-clause-${index}`} value={account.sourceClause} onChange={e => update(account.id, { sourceClause: e.target.value })} placeholder="e.g. Section 7.01(a)" /></Label>
      </Fields>
      <Actions><Button type="button" onClick={() => onChange({ ...value, accounts: value.accounts.filter(item => item.id !== account.id).map(item => item.sweepToId === account.id ? { ...item, sweepToId: '' } : item) })}><Trash2 size={12} />Remove account</Button></Actions>
    </Row>)}
    <Actions>
      {value.accounts.length === 0 && <Button type="button" onClick={addStandard}><Plus size={12} />Add the usual four: collection, lockbox, reserve, payment</Button>}
      <Button type="button" onClick={() => onChange({ ...value, accounts: [...value.accounts, newAccount('COLLECTION', value.accounts[0]?.bank || '')] })}><Plus size={12} />Add an account</Button>
    </Actions>

    <Head><div><h3><CalendarClock size={15} /> Payment calendar</h3></div></Head>
    {!calendar
      ? <Actions><Button type="button" onClick={() => onChange({ ...value, calendar: standardCalendar(lane === 'AMENDMENT' ? '' : effectiveFrom || '') })}><Plus size={12} />Add the payment calendar</Button><Hint>Without one, no collection periods are created for this facility.</Hint></Actions>
      : <Row>
        <Fields>
          <Label htmlFor={`${idPrefix}-first`}>First period starts<DateField id={`${idPrefix}-first`} value={calendar.firstPeriodStart} onChange={next => setCalendar({ firstPeriodStart: next })} /></Label>
          <Label htmlFor={`${idPrefix}-det`}>Determination day<input id={`${idPrefix}-det`} type="number" min="1" max="31" value={calendar.determinationDay} onChange={e => setCalendar({ determinationDay: e.target.value })} /></Label>
          <Label htmlFor={`${idPrefix}-pay`}>Payment day<input id={`${idPrefix}-pay`} type="number" min="1" max="31" value={calendar.paymentDay} onChange={e => setCalendar({ paymentDay: e.target.value })} /></Label>
          <Label htmlFor={`${idPrefix}-conv`}>If not a business day<select id={`${idPrefix}-conv`} value={calendar.convention} onChange={e => setCalendar({ convention: e.target.value as CalendarDraft['convention'] })}><option value="FOLLOWING">Next business day</option><option value="MODIFIED_FOLLOWING">Next, unless it leaves the month</option><option value="PRECEDING">Previous business day</option></select></Label>
          <Label htmlFor={`${idPrefix}-report`}>Servicer report and tape due<select id={`${idPrefix}-report`} value={calendar.reportDue} onChange={e => setCalendar({ reportDue: e.target.value })}><option value="DETERMINATION_DATE">On the determination date</option></select></Label>
          <Label htmlFor={`${idPrefix}-bbc`}>Borrowing base certificate<select id={`${idPrefix}-bbc`} value={calendar.bbcFrequency} onChange={e => setCalendar({ bbcFrequency: e.target.value })}><option value="MONTHLY_AND_EACH_BORROWING">Monthly and with each borrowing request</option><option value="MONTHLY">Monthly</option><option value="WEEKLY">Weekly</option></select></Label>
          <Label htmlFor={`${idPrefix}-cal-clause`}>Source clause<input id={`${idPrefix}-cal-clause`} value={calendar.sourceClause} onChange={e => setCalendar({ sourceClause: e.target.value })} placeholder="e.g. Section 1.01 definitions" /></Label>
        </Fields>
        <Hint>Business days follow the U.S. federal holiday calendar (New York banks).</Hint>
        {preview && preview.periods.length > 0 && <Periods>{preview.periods.map(period => <Period key={period.periodLabel}>
          <span className="m">{monthLabel(period.periodLabel)}</span>
          <div className="r"><span>Period</span><span>{short(period.periodStart)} – {short(period.periodEnd)}</span></div>
          <div className="r"><span>Determination</span><span>{short(period.determinationDate, true)}</span></div>
          <div className="r"><span>Payment</span><span>{short(period.paymentDate, true)}</span></div>
          {period.rollNotes.length > 0 && <div className="note">{period.rollNotes.join('; ')}.</div>}
        </Period>)}</Periods>}
        <Actions><Button type="button" onClick={() => onChange({ ...value, calendar: null })}><Trash2 size={12} />Remove the payment calendar</Button></Actions>
      </Row>}
    {problem && (value.accounts.length > 0 || calendar) && <Hint $tone="warn">{problem}.</Hint>}
  </Wrap>;
};

export default CollectionsSchedulesEditor;
